import { AfterViewInit, Component, ComponentRef, ElementRef, Injector, input, OnChanges, OnDestroy, OnInit, output, SimpleChanges, ViewContainerRef, viewChild, inject } from '@angular/core';
import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import { DriverDocument } from '../../../../shared/interfaces/driver-firestore';
import { SingleMapService } from '../../../../shared/services/single-map.service';
import { debounceTime, fromEventPattern, map, Observable, pairwise, Subject, Subscription } from 'rxjs';
import { Driver } from '../../interfaces/driver';
import { FindDriverTooltipComponent } from './find-driver-tooltip/find-driver-tooltip.component';

@Component({
  selector: 'app-drivers-map',
  standalone: true,
  imports: [],
  templateUrl: './drivers-map.component.html',
  styleUrl: './drivers-map.component.scss',
})
export class DriversMapComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly singleMapService = inject(SingleMapService);

  carMarkers: Map<number, google.maps.marker.AdvancedMarkerElement> = new Map();
  private clusterer!: MarkerClusterer;
  drivers = input.required<Driver[]>();
  viewLatestLocation = input.required<boolean>();

  // All latest drivers locations to set the markers
  latestLocations = input<DriverDocument[]>([]);
  // Only the updated driver location to update that marker
  updatedDriverLocation = input<DriverDocument | null>();

  mapDrivers: Driver[] = [];

  assign = output<Driver>();
  hideFromSearch = output<Driver>();
  infoWindow!: google.maps.InfoWindow;
  private readonly changesSubject$ = new Subject<any>();

  subs: Subscription[] = [];

  // Single map approach
  readonly mapContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('mapDiv');
  private mapInstance: google.maps.Map | null = null;
  private isMapAttached = false;
  private readonly ownerId = `drivers-map`;

  ngOnInit() {
    this.subs.push(
      this.changesSubject$.pipe(debounceTime(2000)).subscribe(changes => {
        if (this.isMapAttached) {
          this.processDataAndAddMarkers();
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['drivers']?.currentValue || (this.viewLatestLocation() && changes['latestLocations']?.currentValue?.length > 0) || changes['viewLatestLocation']) {
      this.processDataAndAddMarkers();
    }
    if (this.viewLatestLocation() && changes['updatedDriverLocation']?.currentValue) {
      this.updateMarkerLocation(changes['updatedDriverLocation'].currentValue);
    }
  }

  updateMarkerLocation(updatedDriverLocation: DriverDocument) {
    const displayedMarker = this.carMarkers.get(updatedDriverLocation.partner_id);
    if (displayedMarker) {
      displayedMarker.position = {
        lat: updatedDriverLocation.latest_active_location._lat,
        lng: updatedDriverLocation.latest_active_location._long,
      };
    }
  }

  ngAfterViewInit() {
    this.attachAndSetupMap();
  }

  attachAndSetupMap() {
    const mapContainerRef = this.mapContainerRef();
    if (this.isMapAttached || !mapContainerRef?.nativeElement) return;
    if (!this.singleMapService.isMapInitialized()) return;

    this.mapInstance = this.singleMapService.acquireMap(this.ownerId, mapContainerRef.nativeElement, {
      ...this.singleMapService.initialMapOptions,
    });

    if (this.mapInstance) {
      this.isMapAttached = true;
      // Setup initial map state
      this.processDataAndAddMarkers(); // Calculate initial drivers and add markers

      this.observeZoomChanges().subscribe(([previousZoom, currentZoom]) => {
        if (currentZoom < previousZoom) {
          this.infoWindow?.close();
        }
      });
    } else {
      console.error('[DriversMap] Failed to attach map.');
    }
  }

  // Central method to determine drivers and refresh markers
  processDataAndAddMarkers() {
    if (!this.isMapAttached || !this.mapInstance) return;
    console.log('DriversMap: Processing data and adding markers...');

    let driversToPlot: Driver[] = [];
    if (!this.viewLatestLocation()) {
      driversToPlot = this.drivers() ?? [];
    } else {
      const latestLocMap = new Map<number, DriverDocument>();
      (this.latestLocations() ?? []).forEach(loc => {
        if (loc.partner_id != null) latestLocMap.set(loc.partner_id, loc);
      });
      driversToPlot = (this.drivers() ?? []).map(d => {
        const latestLoc = latestLocMap.get(d.partnerId);
        if (latestLoc?.latest_active_location?._lat && latestLoc?.latest_active_location?._long) {
          return { ...d, latitude: latestLoc.latest_active_location._lat, longitude: latestLoc.latest_active_location._long };
        }
        return d; // View the original location if no live location found
      });
    }
    this.mapDrivers = driversToPlot;
    this.addCarMarkers();
  }

  observeZoomChanges(): Observable<[number, number]> {
    return fromEventPattern(
      handler => this.mapInstance?.addListener('zoom_changed', handler),
      (handler, listener) => google.maps.event.removeListener(listener),
    ).pipe(
      map(() => this.mapInstance?.getZoom()!),
      pairwise(),
    );
  }

  addCarMarkers() {
    if (!this.isMapAttached || !this.mapInstance) return;
    this.clearMap();
    const bounds = new google.maps.LatLngBounds();
    const filteredDrivers = this.mapDrivers.filter(d => d.latitude && d.longitude);
    const markers = filteredDrivers.map((d: Driver, i: number) => {
      const determineMarkerColor = (matchValue: number) => {
        if (matchValue == 100) {
          return 'success';
        } else if (matchValue < 100 && matchValue > 50) {
          return 'warning';
        } else {
          return 'danger';
        }
      };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: d.latitude ?? 0, lng: d.longitude ?? 0 },
        content: this.createCarIcon(determineMarkerColor(d.bestMatch)),
      });

      bounds.extend(marker.position!);

      marker.addEventListener('click', () => {
        this.openInfoWindow(d, marker);
      });

      this.carMarkers.set(d.partnerId, marker);
      return marker;
    });

    const renderer = {
      render: ({ count, position }: Cluster) => {
        // Custom cluster icon

        return new google.maps.marker.AdvancedMarkerElement({
          position,
          gmpClickable: true,
          content: this.createCarIcon(''),
        });
      },
    };
    this.clusterer = new MarkerClusterer({ markers, map: this.mapInstance, renderer });
  }

  private openInfoWindow(driver: Driver, marker: google.maps.marker.AdvancedMarkerElement) {
    if (this.infoWindow) {
      this.infoWindow.close();
    }

    const tooltipRef = this.createComponentContent(driver);

    this.infoWindow = new google.maps.InfoWindow({
      content: tooltipRef.location.nativeElement,
    });

    this.infoWindow.open(marker.map, marker);
  }

  private createComponentContent(driver: Driver): ComponentRef<FindDriverTooltipComponent> {
    const componentRef = this.viewContainerRef.createComponent(FindDriverTooltipComponent, {
      injector: this.injector,
    });

    componentRef.instance.driver.set(driver);
    componentRef.instance.assign.subscribe(d => this.assign.emit(d));
    componentRef.instance.hide.subscribe(d => {
      this.hideFromSearch.emit(d);
      const driverIndex = this.mapDrivers.findIndex(md => md.partnerId == d.partnerId);
      if (driverIndex) {
        this.mapDrivers.splice(driverIndex, 1);
        this.addCarMarkers();
      }
    });
    componentRef.instance.closed.subscribe(() => this.infoWindow.close());
    return componentRef;
  }

  private createCarIcon(status: 'success' | 'warning' | 'danger' | ''): HTMLElement {
    const div = document.createElement('div');
    div.className = 'car-marker';
    div.innerHTML = `<img class="car-img" src="/assets/google-map-assets/icons/car.svg" alt="Car">
      <div class="car-shadow ${status}"></div>`;
    return div;
  }

  clearMap() {
    this.carMarkers.clear();

    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer = null!;
    }

    this.infoWindow?.close();
  }

  ngOnDestroy() {
    // Cleanup map resources if attached
    if (this.isMapAttached) {
      this.clearMap();
      // Detach map from service
      this.singleMapService.releaseMap(this.ownerId);
      this.isMapAttached = false;
    }
    // Nullify local map instance ref
    this.mapInstance = null;
    // Unsubscribe component subscriptions
    if (this.subs.length > 0) {
      this.subs.forEach(s => s.unsubscribe());
    }
    // Complete subjects
    this.changesSubject$.complete();
  }
}
